<template>
  <div style="background-color: #212121; min-height: 100vh" class="pa-2 pa-md-4">
    <div v-if="userService.hasRight('sm_appointment_management')" class="d-flex justify-end flex-wrap mb-4">
      <v-btn
        :to="{ name: 'homeDashboardAcquisitionManagement' }"
        outlined
        color="#7dd3fc"
        min-height="44"
        class="rounded-lg px-4">
        <v-icon left>mdi-chart-bar</v-icon>Kontakte · Alle Studios
      </v-btn>
    </div>

    <section v-if="!finishedLoading" class="monthly-statistics-loading d-flex align-center justify-center px-2 px-md-6" role="status" aria-live="polite">
      <v-card dark color="#2A2A2A" elevation="8" class="monthly-statistics-loading__card rounded-xl pa-6 pa-md-10 text-center">
        <div class="monthly-statistics-loading__indicator mx-auto mb-6">
          <v-progress-circular indeterminate rotate="-90" size="88" width="4" color="#4f9cf9">
            <v-icon color="#7dd3fc" size="34">mdi-chart-box-outline</v-icon>
          </v-progress-circular>
        </div>
        <div class="text-overline monthly-statistics-loading__eyebrow mb-1">Monatsübersicht</div>
        <h2 class="text-h5 font-weight-black mb-2">Centerstatistiken werden vorbereitet</h2>
        <p class="grey--text text--lighten-1 mb-7">
          Mitglieder, Trainerstatistiken und Arbeitskalender werden für den gewählten Zeitraum zusammengeführt.
        </p>
        <div class="monthly-statistics-loading__skeleton" aria-hidden="true">
          <div v-for="item in 3" :key="'monthly-loading-' + item" class="monthly-statistics-loading__skeleton-card">
            <span class="monthly-statistics-loading__skeleton-icon"></span>
            <div class="flex-grow-1">
              <span class="monthly-statistics-loading__skeleton-line monthly-statistics-loading__skeleton-line--short"></span>
              <span class="monthly-statistics-loading__skeleton-line"></span>
            </div>
          </div>
        </div>
      </v-card>
    </section>

    <section v-else-if="dashboardLoadingError" class="monthly-statistics-loading d-flex align-center justify-center px-2 px-md-6">
      <v-card dark color="#2A2A2A" elevation="8" class="monthly-statistics-loading__card rounded-xl pa-6 pa-md-10 text-center">
        <v-icon color="error" size="52" class="mb-4">mdi-alert-circle-outline</v-icon>
        <h2 class="text-h5 font-weight-black mb-2">Monatsübersicht konnte nicht geladen werden</h2>
        <p class="grey--text text--lighten-1 mb-5">{{ dashboardLoadingError }}</p>
        <v-btn color="primary" rounded depressed class="font-weight-bold px-6" @click="fetchData">
          <v-icon left>mdi-refresh</v-icon>Erneut laden
        </v-btn>
      </v-card>
    </section>

    <section v-else-if="!startOfMonth" class="monthly-statistics-loading d-flex flex-column align-center justify-center px-2 px-md-6">
      <v-card dark color="#2A2A2A" elevation="8" class="monthly-statistics-loading__card rounded-xl pa-6 pa-md-10 text-center">
        <v-icon color="blue-grey lighten-1" size="52" class="mb-4">mdi-chart-box-outline</v-icon>
        <h2 class="text-h5 font-weight-black mb-2">Keine Monatsdaten verfügbar</h2>
        <p class="grey--text text--lighten-1 mb-0">Für den gewählten Zeitraum wurden keine Mitglieder-Monatsdaten gefunden.</p>
      </v-card>
      <center-acquisition-statistics :studio-id="selectedStudioSingle" :range="datePickerRange" style="width: 100%" />
    </section>

    <div v-else class="white--text">
      <v-card-title class="text-h5 font-weight-black px-2 mb-4 d-flex align-center">
        <v-icon left color="primary" large>mdi-view-dashboard</v-icon>
        Überblick Center
      </v-card-title>

      <v-row dense>
        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">Mitglieder ({{ $moment(datePickerRange[0]).format("DD.MMM") }})</span>
              <v-avatar color="rgba(0, 201, 167, 0.1)" size="32">
                <v-icon color="#00c9a7" small>mdi-account-group</v-icon>
              </v-avatar>
            </div>
            <div class="text-h4 font-weight-black mt-auto">
              <number :to="parseFloat(Number(startOfMonth.customersAmount.tac.customers.total).toFixed(2) || 0)"></number>
            </div>
            <div class="accent-line" style="background: #00c9a7"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">Kündigungen</span>
              <v-avatar color="rgba(255, 0, 0, 0.1)" size="32">
                <v-icon color="red" small>mdi-account-remove</v-icon>
              </v-avatar>
            </div>
            <div v-if="finishedLoadingTerminations" class="text-h4 font-weight-black mt-auto">
              <number :to="parseFloat(Number(terminations).toFixed(2) || 0)"></number>
            </div>
            <v-progress-circular v-else indeterminate color="red" size="24"></v-progress-circular>
            <div class="accent-line" style="background: red"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">Neu Verträge</span>
              <v-avatar color="rgba(33, 150, 243, 0.1)" size="32">
                <v-icon color="primary" small>mdi-file-document-edit</v-icon>
              </v-avatar>
            </div>
            <div v-if="finishedLoading" class="text-h4 font-weight-black mt-auto">
              <number :to="parseFloat(Number(newContracts.totalContracts).toFixed(2) || 0)"></number>
            </div>
            <v-progress-circular v-else indeterminate color="primary" size="24"></v-progress-circular>
            <div class="accent-line" style="background: var(--v-primary-base, #1976d2)"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">Online Verträge</span>
              <v-avatar color="rgba(255, 152, 0, 0.1)" size="32">
                <v-icon color="orange" small>mdi-laptop</v-icon>
              </v-avatar>
            </div>
            <div v-if="finishedLoading" class="text-h4 font-weight-black mt-auto">
              <number :to="parseFloat(Number(newContracts.publicContracts).toFixed(2) || 0)"></number>
            </div>
            <v-progress-circular v-else indeterminate color="orange" size="24"></v-progress-circular>
            <div class="accent-line" style="background: orange"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">Soll Wachstum</span>
              <v-avatar color="rgba(0, 175, 166, 0.1)" size="32">
                <v-icon color="#00afa6" small>mdi-trending-up</v-icon>
              </v-avatar>
            </div>
            <div class="text-h4 font-weight-black mt-auto">
              <number :to="parseFloat(Number(growthForMonth).toFixed(2) || 0)"></number>
            </div>
            <div class="accent-line" style="background: #00afa6"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">IST Wachstum</span>
              <v-avatar color="rgba(0, 175, 166, 0.1)" size="32">
                <v-icon color="#00afa6" small>mdi-finance</v-icon>
              </v-avatar>
            </div>
            <div v-if="finishedLoadingTerminations" class="text-h4 font-weight-black mt-auto">
              <number :to="parseFloat(Number(newContracts.totalContracts - terminations).toFixed(2) || 0)"></number>
            </div>
            <v-progress-circular v-else indeterminate color="#00afa6" size="24"></v-progress-circular>
            <div class="accent-line" style="background: #00afa6"></div>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mt-4">
        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">Quadratmeter</span>
              <v-avatar color="rgba(1, 122, 139, 0.1)" size="32">
                <v-icon color="#017a8b" small>mdi-texture-box</v-icon>
              </v-avatar>
            </div>
            <div class="text-h4 font-weight-black d-flex align-baseline mt-auto">
              <number :to="parseFloat(Number(startOfMonth.customersAmount.tac.squareMeter.squareMeterAmount).toFixed(2) || 0)"></number>
              <span class="text-subtitle-1 grey--text ml-1">m²</span>
            </div>
            <div class="accent-line" style="background: #017a8b"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">Mitglieder / m²</span>
              <v-avatar color="rgba(1, 122, 139, 0.1)" size="32">
                <v-icon color="#017a8b" small>mdi-human-capacity-increase</v-icon>
              </v-avatar>
            </div>
            <div class="text-h4 font-weight-black mt-auto">
              {{ Number(startOfMonth.customersAmount.tac.squareMeter.customerAmount).toFixed(2) }}
            </div>
            <div class="accent-line" style="background: #017a8b"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="2">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">Stellen % ({{ $moment(datePickerRange[0]).format("DD.MM") }})</span>
              <v-avatar color="rgba(38, 96, 116, 0.1)" size="32">
                <v-icon color="#266074" small>mdi-briefcase-clock</v-icon>
              </v-avatar>
            </div>
            <div class="text-h4 font-weight-black mt-auto">
              <number :to="parseFloat(Number(startOfMonth.customersAmount.tac.percentage.percentageAmount).toFixed(2) || 0)"></number>%
            </div>
            <div class="accent-line" style="background: #266074"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">Stellen % Aktuell</span>
              <v-avatar color="rgba(38, 96, 116, 0.1)" size="32">
                <v-icon color="#266074" small>mdi-briefcase-check</v-icon>
              </v-avatar>
            </div>
            <div class="text-h4 font-weight-black mt-auto"><number :to="parseFloat(Number(totalPercentages).toFixed(2) || 0)"></number>%</div>
            <div class="accent-line" style="background: #266074"></div>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card dark class="dashboard-card fill-height rounded-xl pa-4" color="#2A2A2A" elevation="4">
            <div class="d-flex justify-space-between align-start mb-2">
              <span class="card-label">Mitglieder pro 100%</span>
              <v-avatar color="rgba(38, 96, 116, 0.1)" size="32">
                <v-icon color="#266074" small>mdi-account-multiple-check</v-icon>
              </v-avatar>
            </div>
            <div class="text-h4 font-weight-black mt-auto">
              <number :to="parseFloat(Number(startOfMonth.customersAmount.tac.percentage.customerAmount * 100).toFixed(2) || 0)"></number>
            </div>
            <div class="accent-line" style="background: #266074"></div>
          </v-card>
        </v-col>
      </v-row>

      <center-acquisition-statistics :studio-id="selectedStudioSingle" :range="datePickerRange" />

      <v-card-title class="text-h6 font-weight-bold px-2 mt-8 mb-2 d-flex align-center flex-wrap">
        Monatsziele der Trainer
        <v-spacer></v-spacer>
        <v-btn color="primary" small rounded class="mt-2 mt-sm-0" @click="openTrainerSelectDialog">
          <v-icon left small>mdi-flag-checkered</v-icon> Ziele bearbeiten
        </v-btn>
      </v-card-title>

      <v-data-table
        dark
        :mobile-breakpoint="mobileTableMode"
        :search="search"
        :loading="!finishedLoading"
        :headers="groupfitnessCoursesTable"
        no-data-text="Keine Trainer gefunden..."
        loading-text="Trainerstatistiken werden geladen..."
        :items="filteredDashboardTrainerInfos"
        :items-per-page="8"
        class="elevation-4 rounded-xl modern-table overflow-hidden"
        style="background-color: #2a2a2a">
        <template v-slot:top>
          <v-row class="pa-4" align="center">
            <v-col cols="12" sm="7" md="6" class="d-flex align-center">
              <v-text-field
                dark
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Suche nach Trainer..."
                outlined
                dense
                hide-details
                color="primary"></v-text-field>
              <v-btn v-if="$vuetify.breakpoint.mobile" icon color="primary" class="ml-3" @click="toggleMobileTableMode">
                <v-icon>mdi-table-sync</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="12" sm="5" md="6" class="text-right">
              <v-tooltip bottom>
                <div class="d-flex flex-column pa-2 white--text">
                  <span class="font-weight-bold mb-1">Ausgeblendete Mitarbeiter:</span>
                  <p v-for="trainer of notShownTrainers" :key="'notshown' + trainer._id" class="mb-0 text-caption">
                    {{ trainer.fullName }}
                  </p>
                  <span v-if="!notShownTrainers.length">Niemand ausgeblendet</span>
                </div>
                <template v-slot:activator="{ props, on }">
                  <v-btn v-bind="props" v-on="on" icon color="grey"><v-icon>mdi-information-outline</v-icon></v-btn>
                </template>
              </v-tooltip>
            </v-col>
          </v-row>
        </template>

        <template v-slot:item.percentage="{ item }">
          <v-tooltip bottom>
            <div class="d-flex flex-column white--text">
              <span v-for="(pItem, idx) in item.allPercentages" :key="pItem._id + item.fullName + idx">
                {{ (storeStudios.find((s) => s._id === pItem._id)?.name || "zus. Team") + ": " + Number(pItem.percentage).toFixed(2) + "%" }}
              </span>
            </div>
            <template v-slot:activator="{ props, on }">
              <v-chip color="rgba(255,255,255,0.1)" v-bind="props" v-on="on" class="font-weight-bold" dark
                >{{ Number(item.percentage).toFixed(2) }}%</v-chip
              >
            </template>
          </v-tooltip>
        </template>

        <template v-slot:item.contracts="{ item }">
          <v-tooltip bottom>
            <div class="white--text">
              <p v-for="(count, name) in item.studios" :key="name" class="mb-0">{{ name }}: {{ Number(count).toFixed(2) }}</p>
            </div>
            <template v-slot:activator="{ props, on }">
              <v-chip
                v-bind="props"
                v-on="on"
                :color="getTargetColor(item.contracts, item.trainerGoal?.goals.contracts)"
                class="font-weight-bold"
                dark>
                {{ Number(item.contracts).toFixed(2) }} /
                {{ item.trainerGoal?.goals.contracts != null ? Number(item.trainerGoal.goals.contracts).toFixed(2) : "-" }}
              </v-chip>
            </template>
          </v-tooltip>
        </template>

        <template v-slot:item.avgPackages="{ item }">
          <v-tooltip bottom>
            <div class="white--text">
              <p class="mb-0">Gesamt verkauft: {{ (item.avgPackages * item.contracts).toFixed(2) }}</p>
            </div>
            <template v-slot:activator="{ props, on }">
              <v-chip v-bind="props" v-on="on" :color="getPackageColor(item.avgPackages)" class="font-weight-bold" dark>
                {{ Number(item.avgPackages).toFixed(2) }}
              </v-chip>
            </template>
          </v-tooltip>
        </template>

        <template v-slot:item.packageCount="{ item }">
          <v-chip :color="getTargetColor(item.packageCount, item.trainerGoal?.goals.packages)" class="font-weight-bold" dark>
            {{ Number(item.packageCount).toFixed(2) }} /
            {{ item.trainerGoal?.goals.packages != null ? Number(item.trainerGoal.goals.packages).toFixed(2) : "-" }}
          </v-chip>
        </template>

        <template v-slot:item.medicalHistory="{ item }">
          <v-chip :color="getTargetColor(item.medicalHistory, item.trainerGoal?.goals.anamneses)" class="font-weight-bold" dark>
            {{ Number(item.medicalHistory).toFixed(2) }} /
            {{ item.trainerGoal?.goals.anamneses != null ? Number(item.trainerGoal.goals.anamneses).toFixed(2) : "-" }}
          </v-chip>
        </template>

        <template v-slot:item.workouts="{ item }">
          <v-chip :color="getTargetColor(item.workouts, item.trainerGoal?.goals.workouts)" class="font-weight-bold" dark>
            {{ Number(item.workouts).toFixed(2) }} /
            {{ item.trainerGoal?.goals.workouts != null ? Number(item.trainerGoal.goals.workouts).toFixed(2) : "-" }}
          </v-chip>
        </template>

        <template v-slot:item.workoutsPerCustomer="{ item }">
          <v-chip :color="getTargetColor(item.workoutsPerCustomer, item.trainerGoal?.goals.workoutsPerCustomer)" class="font-weight-bold" dark>
            {{ Number(item.workoutsPerCustomer).toFixed(2) }} /
            {{ item.trainerGoal?.goals.workoutsPerCustomer != null ? Number(item.trainerGoal.goals.workoutsPerCustomer).toFixed(2) : "-" }}
          </v-chip>
        </template>

        <template v-slot:item.interactions.totalInteractions="{ item }">
          <v-chip :color="getTargetColor(item.interactions.totalInteractions, item.trainerGoal?.goals.interactions)" class="font-weight-bold" dark>
            {{ Number(item.interactions.totalInteractions).toFixed(2) }} /
            {{ item.trainerGoal?.goals.interactions != null ? Number(item.trainerGoal.goals.interactions).toFixed(2) : "-" }}
          </v-chip>
        </template>

        <template v-slot:item.appointments="{ item }">
          <v-menu bottom offset-y :open-on-hover="!$vuetify.breakpoint.mobile">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                :color="getTargetColor(appointmentActual(item), item.trainerGoal?.goals.appointmentsTarget)"
                :aria-label="'Termine nach Studio für ' + item.fullName"
                link
                class="font-weight-bold"
                dark>
                {{ Number(appointmentActual(item)).toFixed(2) }} /
                {{ item.trainerGoal?.goals.appointmentsTarget != null ? Number(item.trainerGoal.goals.appointmentsTarget).toFixed(2) : "-" }}
                <v-icon x-small right>{{ appointmentSourceIcon(item) }}</v-icon>
              </v-chip>
            </template>
            <v-card dark color="#303030" class="pa-4" max-width="360">
              <div class="font-weight-bold mb-2">Termine nach Studio</div>
              <p v-for="studio in item.appointmentsByStudio || []" :key="studio.studioId" class="mb-1">
                {{ studio.name }}: {{ Number(studio.count).toFixed(2) }}
              </p>
              <p v-if="item.appointmentsWithoutStudio" class="mb-1">
                Ohne Studiozuordnung (historisch): {{ Number(item.appointmentsWithoutStudio).toFixed(2) }}
              </p>
              <p v-if="!appointmentActual(item)" class="mb-1">Keine Termine erfasst.</p>
              <div class="text-caption grey--text text--lighten-1 mt-3">
                {{ appointmentSourceLabel(item) }} · ohne stornierte, gelöschte und No-Show-Termine.
              </div>
            </v-card>
          </v-menu>
        </template>

        <template v-slot:item.upgrades="{ item }">
          <v-chip :color="getTargetColor(item.upgrades, item.trainerGoal?.goals.upgrades)" class="font-weight-bold" dark>
            {{ Number(item.upgrades).toFixed(2) }} /
            {{ item.trainerGoal?.goals.upgrades != null ? Number(item.trainerGoal.goals.upgrades).toFixed(2) : "-" }}
          </v-chip>
        </template>
      </v-data-table>

      <v-card-title class="text-h6 font-weight-bold px-2 mt-8 mb-2">
        11-Tage-Statistiken
        <v-chip small class="ml-3 font-weight-bold" color="rgba(255,255,255,0.1)" dark>20% unter Ø Rot | 20% über Ø Grün</v-chip>
      </v-card-title>
      <v-data-table
        dark
        :mobile-breakpoint="mobileTableMode"
        :loading="!finishedLoadingElevenDaysData"
        :headers="elevenDaysHeaders"
        :items="elevenDaysData"
        class="elevation-4 rounded-xl modern-table"
        style="background-color: #2a2a2a"
        :items-per-page="8">
        <template v-slot:item.activated="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" :color="getDeviationColor(item.activated, averages.activated)" class="font-weight-bold" dark>
                {{ Number(item.activated).toFixed(2) }}
              </v-chip>
            </template>
            <span class="white--text">Ø Aktiviert: {{ Number(averages.activated).toFixed(2) }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.accepted="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" :color="getDeviationColor(item.accepted, averages.accepted)" class="font-weight-bold" dark>
                {{ Number(item.accepted).toFixed(2) }}
              </v-chip>
            </template>
            <span class="white--text">Ø Abgeschlossen: {{ Number(averages.accepted).toFixed(2) }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.declined="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" :color="getDeviationColor(item.declined, averages.declined)" class="font-weight-bold" dark>
                {{ Number(item.declined).toFixed(2) }}
              </v-chip>
            </template>
            <span class="white--text">Ø Kein Abschluss: {{ Number(averages.declined).toFixed(2) }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.anamnese="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" :color="getDeviationColor(item.anamnese, averages.anamnese)" class="font-weight-bold" dark>
                {{ Number(item.anamnese).toFixed(2) }}
              </v-chip>
            </template>
            <span class="white--text">Ø Anamnese: {{ Number(averages.anamnese).toFixed(2) }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.anamneseEleven="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" :color="getDeviationColor(item.anamneseEleven, averages.anamneseEleven)" class="font-weight-bold" dark>
                {{ Number(item.anamneseEleven).toFixed(2) }}
              </v-chip>
            </template>
            <span class="white--text">Ø Anamnese 11 Tage: {{ Number(averages.anamneseEleven).toFixed(2) }}</span>
          </v-tooltip>
        </template>

        <template v-slot:item.quote="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip v-bind="attrs" v-on="on" :color="getDeviationColor(item.quote, averages.quote)" class="font-weight-bold" dark>
                {{ Number(item.quote).toFixed(2) }}%
              </v-chip>
            </template>
            <span class="white--text">Ø Quote: {{ Number(averages.quote).toFixed(2) }}%</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <v-card-title class="text-h6 font-weight-bold px-2 mt-8 mb-2">Überstunden und Ferienstunden</v-card-title>
      <v-data-table
        dark
        :mobile-breakpoint="mobileTableMode"
        :headers="overtimeVacationHeaders"
        :items="overtimeVacationItems"
        class="elevation-4 rounded-xl modern-table mb-8"
        style="background-color: #2a2a2a"
        :items-per-page="8">
        <template v-slot:item.percentage="{ item }">
          <v-chip class="font-weight-bold" color="rgba(255,255,255,0.1)" dark>{{ Number(item.percentage).toFixed(2) }}%</v-chip>
        </template>
        <template v-slot:item.overTime="{ item }">
          <v-chip class="font-weight-bold" color="rgba(255,255,255,0.1)" dark v-if="item.overTime !== 'keine hinterlegt'">{{
            Number(item.overTime).toFixed(2)
          }}</v-chip>
          <v-chip class="font-weight-bold" color="rgba(255,255,255,0.1)" dark v-else>keine hinterlegt</v-chip>
        </template>
        <template v-slot:item.vacationHours="{ item }">
          <v-chip class="font-weight-bold" color="rgba(255,255,255,0.1)" dark v-if="item.vacationHours !== 'keine hinterlegt'">
            {{ Number(item.vacationHours).toFixed(2) }}h |
            {{ item.timeTrackerPercentage > 0 ? (item.vacationHours / ((8.4 / 100) * item.timeTrackerPercentage)).toFixed(2) : "0.00" }} Tage
          </v-chip>
          <v-chip class="font-weight-bold" color="rgba(255,255,255,0.1)" dark v-else>keine hinterlegt</v-chip>
        </template>
        <template v-slot:item.vacationDaysCount="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ attrs, on }">
              <v-chip v-bind="attrs" v-on="on" class="font-weight-bold" color="primary" outlined dark
                >{{ Number(item.vacationDaysCount).toFixed(2) }} Tage</v-chip
              >
            </template>
            <div v-for="date in item.vacationDatesList" :key="date.date" class="text-caption white--text">
              {{ $moment(date.date).format("DD.MM.YYYY") + " " + date.reason }}
            </div>
          </v-tooltip>
        </template>
      </v-data-table>

      <v-card dark color="#2A2A2A" elevation="4" class="utilization-panel rounded-xl mt-8 mb-8 overflow-hidden">
        <v-card-title class="utilization-panel__header d-flex align-center flex-wrap px-4 px-md-6 py-5">
          <div class="d-flex align-center mr-4">
            <v-avatar color="rgba(79, 156, 249, 0.16)" size="42" class="mr-3">
              <v-icon color="#4f9cf9">mdi-chart-box-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-black">Arbeitskalender-Auslastung</div>
              <div class="text-caption grey--text text--lighten-1">Alle Center im gewählten Zeitraum vergleichen</div>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-chip small outlined color="#4f9cf9" class="font-weight-bold mt-2 mt-sm-0 mr-2"> {{ comparisonMeta.centerCount || 0 }} Center </v-chip>
          <v-chip v-if="comparisonAnonymousCenterCount" small outlined color="#94a3b8" class="font-weight-bold mt-2 mt-sm-0">
            <v-icon left x-small>mdi-incognito</v-icon>{{ comparisonAnonymousCenterCount }} anonymisiert
          </v-chip>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-4 pa-md-6">
          <v-row dense align="center" class="utilization-controls">
            <v-col v-if="canAnonymizeOtherCenters" cols="12" class="pb-3">
              <div
                class="utilization-presentation-mode d-flex align-center flex-wrap pa-3"
                :class="{ 'utilization-presentation-mode--active': comparisonPresentationModeActive }">
                <v-icon :color="comparisonPresentationModeActive ? '#4f9cf9' : '#94a3b8'" class="mr-3">
                  mdi-presentation
                </v-icon>
                <div class="utilization-presentation-mode__copy flex-grow-1 mr-4">
                  <div class="font-weight-bold">Präsentationsmodus</div>
                  <div class="text-caption grey--text text--lighten-1">{{ comparisonPresentationDescription }}</div>
                  <v-chip
                    v-if="comparisonPresentationModeActive && selectedComparisonCenter"
                    small
                    color="rgba(34, 211, 238, 0.16)"
                    text-color="#a5f3fc"
                    class="utilization-own-center-chip font-weight-black mt-2">
                    <v-icon left x-small>mdi-map-marker-star-outline</v-icon>
                    Dein Center · {{ selectedComparisonCenter.label }}
                  </v-chip>
                </div>
                <v-switch
                  v-model="anonymizeOtherCenters"
                  label="Andere Center anonymisieren"
                  color="#4f9cf9"
                  class="utilization-presentation-mode__switch mt-0 pt-0"
                  inset
                  hide-details></v-switch>
              </div>
            </v-col>

            <v-col cols="12" sm="6" lg="3">
              <v-select
                v-model="comparisonView"
                :items="comparisonViewOptions"
                label="Darstellung"
                prepend-inner-icon="mdi-chart-areaspline"
                outlined
                dense
                hide-details></v-select>
            </v-col>

            <v-col v-if="comparisonView === 'bars'" cols="12" sm="6" lg="5">
              <v-select
                v-model="comparisonMetric"
                :items="comparisonMetricOptions"
                label="Kennzahl"
                prepend-inner-icon="mdi-tune-variant"
                outlined
                dense
                hide-details></v-select>
            </v-col>

            <v-col v-if="comparisonView === 'bars'" cols="12" sm="6" lg="4">
              <v-select
                v-model="comparisonSort"
                :items="comparisonSortOptions"
                label="Sortierung"
                prepend-inner-icon="mdi-sort"
                outlined
                dense
                hide-details></v-select>
            </v-col>

            <v-col v-if="comparisonView !== 'bars'" cols="12" sm="6" lg="3">
              <v-select
                v-model="radarGroup"
                :items="radarGroupOptions"
                label="Profil"
                prepend-inner-icon="mdi-radar"
                outlined
                dense
                hide-details></v-select>
            </v-col>

            <v-col v-if="comparisonView === 'centerRadar'" cols="12" lg="6">
              <v-select
                v-model="centerProfileKey"
                :items="centerProfileOptions"
                label="Eigenes Center"
                prepend-inner-icon="mdi-office-building-marker-outline"
                outlined
                dense
                hide-details></v-select>
            </v-col>

            <v-col v-if="comparisonView === 'employeeRadar'" cols="12" lg="6">
              <v-select
                v-model="employeeProfileKey"
                :items="employeeProfileOptions"
                label="Mitarbeiter im eigenen Center"
                prepend-inner-icon="mdi-account-outline"
                outlined
                dense
                hide-details></v-select>
            </v-col>
          </v-row>

          <v-alert v-if="comparisonError" type="error" outlined class="mt-5 mb-0">
            {{ comparisonError }}
            <v-btn small text color="error" class="ml-2" @click="fetchWorkCalendarCenterUtilization">Erneut laden</v-btn>
          </v-alert>

          <div v-else-if="comparisonLoading" class="utilization-state utilization-state--loading text-center py-12 px-4" role="status" aria-live="polite">
            <div class="utilization-state__indicator mx-auto">
              <v-progress-circular indeterminate rotate="-90" color="#4f9cf9" size="58" width="4">
                <v-icon color="#7dd3fc" size="23">mdi-chart-areaspline</v-icon>
              </v-progress-circular>
            </div>
            <div class="mt-4 font-weight-bold white--text">Arbeitskalenderdaten aller Center werden geladen</div>
            <div class="mt-1 text-caption grey--text text--lighten-1">Auslastungen und Zeitprofile werden sicher zusammengeführt.</div>
            <v-progress-linear indeterminate rounded height="4" color="#4f9cf9" class="utilization-state__progress mx-auto mt-5"></v-progress-linear>
          </div>

          <div v-else-if="!comparisonCenters.length" class="utilization-state text-center py-12">
            <v-icon color="grey" size="42">mdi-chart-box-outline</v-icon>
            <div class="mt-3 grey--text text--lighten-1">Für diesen Zeitraum sind keine Centerdaten verfügbar.</div>
          </div>

          <template v-else>
            <div class="utilization-definition d-flex align-start mt-5 mb-3">
              <v-icon small color="blue-grey lighten-2" class="mr-2 mt-1">mdi-information-outline</v-icon>
              <span class="text-caption grey--text text--lighten-1">{{ activeComparisonDefinition }}</span>
            </div>

            <div v-if="comparisonView === 'bars'" class="utilization-chart-shell" :style="{ minHeight: comparisonBarHeight + 'px' }">
              <apexchart
                :key="comparisonBarChartKey"
                type="bar"
                width="100%"
                :height="comparisonBarHeight"
                :options="comparisonBarOptions"
                :series="comparisonBarSeries"></apexchart>
            </div>

            <div v-else-if="activeRadarItem" class="utilization-radar-shell">
              <div class="d-flex justify-center align-center flex-wrap mb-1">
                <v-chip
                  :color="activeRadarPalette.soft"
                  :text-color="activeRadarPalette.text"
                  class="font-weight-bold"
                  :class="{ 'utilization-radar-profile--own': activeRadarItem.presentationOwnCenter }">
                  <v-icon left small>{{ comparisonView === "employeeRadar" ? "mdi-account" : "mdi-office-building" }}</v-icon>
                  {{ activeRadarTitle }}
                </v-chip>
                <v-chip
                  v-if="activeRadarItem.presentationOwnCenter"
                  small
                  color="rgba(34, 211, 238, 0.16)"
                  text-color="#a5f3fc"
                  class="utilization-own-center-chip font-weight-black ml-2">
                  <v-icon left x-small>mdi-star-circle</v-icon>Dein Center
                </v-chip>
                <span v-if="activeRadarSubtitle" class="text-caption grey--text text--lighten-1 ml-2">{{ activeRadarSubtitle }}</span>
                <v-chip small outlined :color="activeRadarPalette.line" class="font-weight-bold ml-2">
                  <v-icon left x-small>mdi-arrow-expand-vertical</v-icon>
                  Skala 0–{{ comparisonRadarMax }} %
                </v-chip>
              </div>
              <apexchart
                :key="comparisonRadarChartKey"
                type="radar"
                width="100%"
                :height="comparisonRadarHeight"
                :options="comparisonRadarOptions"
                :series="comparisonRadarSeries"
                @dataPointSelection="handleTaskBreakdownDataPointSelection"></apexchart>

              <div
                v-if="availableRadarBreakdowns.length"
                class="utilization-task-trigger d-flex align-center justify-center flex-wrap px-3 pb-4">
                <div class="utilization-task-trigger__hint d-flex align-center mr-3 mb-2 mb-sm-0">
                  <v-icon small color="#94a3b8" class="mr-2">mdi-gesture-tap</v-icon>
                  <span class="text-caption grey--text text--lighten-1">Tippe auf Termine, Ämtli oder Aufgaben – oder öffne die Details hier.</span>
                </div>
                <div class="d-flex align-center justify-center flex-wrap">
                  <v-btn
                    v-for="breakdown in availableRadarBreakdowns"
                    :key="breakdown.metricKey"
                    small
                    rounded
                    outlined
                    :color="breakdown.color"
                    class="font-weight-bold ma-1"
                    @click="toggleTaskBreakdown(breakdown.metricKey)">
                    <v-icon left small>{{ taskBreakdownOpen && activeBreakdownMetricKey === breakdown.metricKey ? "mdi-chevron-up" : breakdown.icon }}</v-icon>
                    {{
                      taskBreakdownOpen && activeBreakdownMetricKey === breakdown.metricKey
                        ? breakdown.label + "-Details schliessen"
                        : breakdown.label + " aufschlüsseln"
                    }}
                  </v-btn>
                </div>
              </div>

              <v-expand-transition>
                <section
                  v-if="taskBreakdownOpen && activeRadarBreakdown"
                  class="utilization-task-detail px-3 px-md-6 pt-5 pb-6"
                  :style="{
                    '--breakdown-color': activeRadarBreakdown.color,
                    '--breakdown-soft': activeRadarBreakdown.soft,
                    '--breakdown-text': activeRadarBreakdown.textColor,
                  }">
                  <div class="utilization-task-detail__header d-flex align-start justify-space-between flex-wrap">
                    <div class="mr-4 mb-3">
                      <div class="text-overline utilization-task-detail__eyebrow">{{ activeRadarBreakdown.label }}-Drill-down</div>
                      <h3 class="text-h6 font-weight-bold mb-1">{{ activeRadarBreakdown.question }} {{ activeRadarTitle }}?</h3>
                      <div class="text-caption grey--text text--lighten-1">
                        Die Werte im Detailnetz zeigen den Anteil an der gesamten {{ activeRadarBreakdown.timeLabel }}.
                      </div>
                    </div>
                    <div class="d-flex align-center flex-wrap mb-3">
                      <v-chip
                        small
                        :color="activeRadarBreakdown.soft"
                        :text-color="activeRadarBreakdown.textColor"
                        class="font-weight-bold mr-2 mb-2">
                        {{ Number(activeRadarItem.metrics[activeRadarBreakdown.metricKey] || 0).toFixed(1) }} % der Schichtzeit
                      </v-chip>
                      <v-chip small outlined color="#94a3b8" class="font-weight-bold mb-2">
                        {{ formatTaskDuration(activeRadarBreakdown.summary.minutes) }} · {{ formatTaskCount(activeRadarBreakdown.summary.count) }}
                      </v-chip>
                    </div>
                  </div>

                  <apexchart
                    v-if="taskBreakdownUsesRadar"
                    :key="taskBreakdownRadarChartKey"
                    type="radar"
                    width="100%"
                    :height="taskBreakdownRadarHeight"
                    :options="taskBreakdownRadarOptions"
                    :series="taskBreakdownRadarSeries"></apexchart>

                  <v-alert v-else dense outlined :color="activeRadarBreakdown.color" class="mt-3 mb-4">
                    Für ein lesbares Spinnennetz braucht es mindestens drei verschiedene {{ activeRadarBreakdown.itemPlural }}. Die vorhandenen
                    {{ activeRadarBreakdown.itemPlural }} stehen vollständig unten.
                  </v-alert>

                  <div class="utilization-task-list mt-2">
                    <div
                      v-for="task in activeTaskBreakdown"
                      :key="task.key"
                      class="utilization-task-list__item d-flex align-center px-3 py-2">
                      <span class="utilization-task-list__dot mr-3"></span>
                      <div class="flex-grow-1 min-width-0 mr-3">
                        <div class="font-weight-bold text-body-2 text-truncate" :title="task.label">{{ task.label }}</div>
                        <div class="text-caption grey--text text--lighten-1">
                          {{ formatTaskDuration(task.minutes) }} · {{ formatTaskCount(task.count) }} ·
                          {{ Number(task.shiftRate || 0).toFixed(1) }} % der Schichtzeit
                        </div>
                      </div>
                      <strong class="utilization-task-list__rate">{{ Number(task.shareRate || 0).toFixed(1) }} %</strong>
                    </div>
                  </div>
                </section>
              </v-expand-transition>
            </div>

            <v-alert v-else type="info" outlined class="mt-5 mb-0">
              Für die Mitarbeiteransicht sind nur Mitarbeitende aus namentlich sichtbaren Centern verfügbar.
            </v-alert>

            <div class="utilization-privacy d-flex align-start mt-4 pa-3 rounded-lg">
              <v-icon small color="blue-grey lighten-2" class="mr-2 mt-1">mdi-shield-lock-outline</v-icon>
              <span class="text-caption grey--text text--lighten-1">
                Center ausserhalb deines Zugriffs werden bereits auf dem Server anonymisiert. Deren Namen, IDs und Mitarbeiterdaten werden nicht
                übertragen.
                <template v-if="comparisonPresentationModeActive">
                  Zusätzlich werden in dieser Ansicht alle anderen sichtbaren Center und deren Mitarbeitende neutral bezeichnet.
                </template>
              </span>
            </div>
          </template>
        </v-card-text>
      </v-card>

      <v-dialog v-model="showTrainerSelectDialog" max-width="500px">
        <v-card dark color="#2A2A2A" class="rounded-xl">
          <v-card-title class="font-weight-bold pb-2">Ziele bearbeiten</v-card-title>
          <v-card-text class="pt-4">
            <v-menu v-model="monthMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="goalMonth"
                  label="Monat auswählen"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  color="primary"></v-text-field>
              </template>
              <v-date-picker
                v-model="goalMonth"
                type="month"
                @input="
                  monthMenu = false
                  fetchDialogTrainers()
                "
                color="primary"
                dark></v-date-picker>
            </v-menu>

            <v-divider class="my-2" style="border-color: rgba(255, 255, 255, 0.1)"></v-divider>
            <div class="text-subtitle-1 mb-2 font-weight-bold">Trainer auswählen:</div>

            <div v-if="isFetchingDialogTrainers" class="text-center py-6">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>

            <v-list dark color="transparent" v-else>
              <v-list-item
                v-for="t in dialogTrainers"
                :key="'dlgt_' + t._id"
                @click="openTargetDialog(t)"
                class="mb-2"
                style="background: rgba(255, 255, 255, 0.05); border-radius: 8px">
                <v-list-item-avatar>
                  <img :src="t.picture ? '/img/profilbilder' + t.picture : '/img/einmalig/imageUploadPlaceholderPFP.jpg'" />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">{{ t.fullName }}</v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    <span :class="t.trainerGoal?.goals ? 'green--text' : 'orange--text'">
                      {{ t.trainerGoal?.goals ? "Ziele für diesen Monat hinterlegt" : "Noch keine Ziele" }}
                    </span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon color="grey">mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
              <div v-if="!dialogTrainers.length" class="text-center grey--text py-4">Keine Trainer für diesen Monat gefunden.</div>
            </v-list>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text color="grey" @click="showTrainerSelectDialog = false">Schliessen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showTargetDialog" max-width="500px">
        <v-card dark color="#2A2A2A" class="rounded-xl" v-if="selectedTrainerForGoal">
          <v-card-title class="font-weight-bold"
            >Ziele für {{ selectedTrainerForGoal.fullName }}
            <span class="text-caption grey--text ml-2 mt-2">({{ $moment(goalMonth).format("MMM YYYY") }})</span></v-card-title
          >

          <v-row no-gutters class="px-4 mb-4">
            <v-col class="text-center" cols="6">
              <span class="font-weight-bold text-caption grey--text text-uppercase">Sollstunden</span>
              <p class="font-weight-bold primary--text mb-0">{{ workHours }}</p>
            </v-col>
            <v-col class="text-center" cols="6">
              <span class="font-weight-bold text-caption grey--text text-uppercase">Überstunden</span>
              <p class="font-weight-bold primary--text mb-0">
                {{ typeof selectedTrainerForGoal.overTime === "number" ? selectedTrainerForGoal.overTime : "0" }} Stunden
              </p>
            </v-col>
          </v-row>

          <v-card-text>
            <v-form>
              <v-row dense>
                <v-col cols="6"
                  ><v-text-field v-model.number="targets.contracts" label="Verträge" type="number" outlined dense color="primary"
                /></v-col>
                <v-col cols="6"
                  ><v-text-field v-model.number="targets.anamneses" label="Anamnesebögen" type="number" outlined dense color="primary"
                /></v-col>
                <v-col cols="6"
                  ><v-text-field v-model.number="targets.packages" label="Anzahl Pakete" type="number" outlined dense color="primary"
                /></v-col>
                <v-col cols="6"
                  ><v-text-field v-model.number="targets.workouts" label="Trainingspläne" type="number" outlined dense color="primary"
                /></v-col>
                <v-col cols="6"
                  ><v-text-field v-model.number="targets.workoutsPerCustomer" label="Versch. Kunden TP" type="number" outlined dense color="primary"
                /></v-col>
                <v-col cols="6"
                  ><v-text-field v-model.number="targets.interactions" label="Kundeninteraktionen" type="number" outlined dense color="primary"
                /></v-col>
                <v-col cols="12"
                  ><v-text-field v-model.number="targets.upgrades" label="Upgrades" type="number" outlined dense color="primary"
                /></v-col>
                <v-col cols="6"
                  ><v-text-field v-model.number="targets.appointmentsTarget" label="Termine (Ziel)" type="number" outlined dense color="primary"
                /></v-col>
                <v-col cols="6">
                  <div class="appointment-actual-readonly">
                    <v-icon color="primary">{{ appointmentSourceIcon(selectedTrainerForGoal) }}</v-icon>
                    <div>
                      <small>Termine (Ist) · nicht editierbar</small>
                      <strong>{{ appointmentActual(selectedTrainerForGoal) }}</strong>
                      <span>{{ appointmentSourceLabel(selectedTrainerForGoal) }}</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text color="grey" @click="showTargetDialog = false">Abbrechen</v-btn>
            <v-btn color="primary" depressed class="rounded-lg px-6 font-weight-bold" @click="saveTargets">Speichern</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import api from "../../../api"
import { mapState } from "vuex"
import userService from "@/services/userService.js"
import VueApexCharts from "vue-apexcharts"
import CenterAcquisitionStatistics from "./CenterAcquisitionStatistics.vue"

export default {
  name: "dashboard",
  components: { apexchart: VueApexCharts, CenterAcquisitionStatistics },
  props: {
    selectedStudioSingle: { type: String },
    datePickerRange: { type: Array },
  },
  data() {
    return {
      userService,
      tab: 0,
      notShownTrainers: [],
      activeSorting: "",
      search: "",
      percentagesTo100: false,
      dashboardTrainerInfos: [],
      finishedLoading: false,
      finishedLoadingTerminations: false,
      finishedLoadingElevenDaysData: false,
      dashboardLoadingError: "",
      startOfMonth: null,
      newContracts: { totalContracts: 0, publicContracts: 0 },
      terminations: null,
      growthForMonth: null,
      elevenDaysData: [],
      trainerVacations: [],
      totalPercentages: 0,

      comparisonLoading: false,
      comparisonError: "",
      comparisonRequestSerial: 0,
      workCalendarComparison: { meta: {}, centers: [], employees: [] },
      comparisonView: "bars",
      comparisonMetric: "whiteSpaceRate",
      comparisonSort: "desc",
      radarGroup: "time",
      centerProfileKey: null,
      employeeProfileKey: null,
      taskBreakdownOpen: false,
      activeBreakdownMetricKey: null,
      anonymizeOtherCenters: false,
      comparisonViewOptions: [
        { text: "Centervergleich · Balken", value: "bars" },
        { text: "Centerprofil · Spinnennetz", value: "centerRadar" },
        { text: "Mitarbeiterprofil · Spinnennetz", value: "employeeRadar" },
      ],
      comparisonMetricOptions: [
        {
          text: "Termin-Auslastung",
          value: "appointmentRate",
          color: "#4f9cf9",
          definition:
            "Gebuchte Kundentermine inklusive Personaltraining im Verhältnis zur Netto-Schichtzeit. No-shows bleiben gebucht, stornierte Termine nicht.",
        },
        {
          text: "Trainerpoint-Auslastung",
          value: "trainerpointRate",
          color: "#20c9a0",
          definition: "Eingeplante Trainerpoint-Zeit innerhalb der Netto-Schichtzeit.",
        },
        {
          text: "Theken-Auslastung",
          value: "thekeRate",
          color: "#f59e0b",
          definition: "Zeit der Aufgabe mit dem exakt geschriebenen Titel «Theke» innerhalb der Netto-Schichtzeit.",
        },
        {
          text: "Ämtli-Auslastung",
          value: "amtliRate",
          color: "#a78bfa",
          definition: "Eingeplante Ämtli-Zeit innerhalb der Netto-Schichtzeit.",
        },
        {
          text: "Aufgaben-Auslastung",
          value: "taskRate",
          color: "#f472b6",
          definition:
            "Zeit aller übrigen Arbeitsaufträge innerhalb der Netto-Schichtzeit; Trainerpoint, Theke und Ämtli werden nicht doppelt gezählt.",
        },
        {
          text: "Weisse Fläche · nichts gebucht",
          value: "whiteSpaceRate",
          color: "#94a3b8",
          definition:
            "Anteil der Netto-Schichtzeit ohne Termin, Trainerpoint, Theke, Ämtli oder sonstigen Kalendereintrag. Überschneidungen werden nur einmal gerechnet.",
        },
        {
          text: "Frühschichten",
          value: "morningRate",
          color: "#38bdf8",
          definition: "Anteil der als Frühschicht erkannten Schichten an allen Schichten des Centers.",
        },
        {
          text: "Spätschichten",
          value: "lateRate",
          color: "#818cf8",
          definition: "Anteil der als Spätschicht erkannten Schichten an allen Schichten des Centers.",
        },
        {
          text: "Splitschichten",
          value: "splitRate",
          color: "#f59e0b",
          definition: "Anteil der als Split erkannten Schichten an allen Schichten des Centers.",
        },
        {
          text: "Wochenendschichten",
          value: "weekendRate",
          color: "#20c9a0",
          definition: "Anteil der Schichten am Samstag oder Sonntag an allen Schichten des Centers.",
        },
      ],
      comparisonSortOptions: [
        { text: "Höchster Wert zuerst", value: "desc" },
        { text: "Tiefster Wert zuerst", value: "asc" },
        { text: "Name A–Z", value: "name" },
      ],
      radarGroupOptions: [
        { text: "Zeitbindung", value: "time" },
        { text: "Schichtmix", value: "shifts" },
      ],

      // --- Neue Variablen für Mobile Tabellen Ansicht ---
      mobileTableMode: "0",

      // --- Neue Variablen für Trainer Ziele ---
      showTrainerSelectDialog: false,
      showTargetDialog: false,
      monthMenu: false,
      goalMonth: "",
      isFetchingDialogTrainers: false,
      dialogTrainers: [],
      selectedTrainerForGoal: null,
      workHours: "",
      vacationDays: 0,
      targets: {
        contracts: null,
        anamneses: null,
        packages: null,
        workouts: null,
        workoutsPerCustomer: null,
        interactions: null,
        upgrades: null,
        appointmentsTarget: null,
        appointmentsActual: null,
      },

      elevenDaysHeaders: [
        { text: "Trainer", value: "trainer", width: "150px" },
        { text: "Anz. 11 Tage Aktiviert", value: "activated" },
        { text: "Abgeschlossen", value: "accepted" },
        { text: "Kein Abschluss", value: "declined" },
        { text: "Anamnese", value: "anamnese" },
        { text: "Anamnese 11. Tage", value: "anamneseEleven" },
        { text: "Quote", value: "quote" },
        { text: "Quote (studio)", value: "studioQuote" },
      ],
      groupfitnessCoursesTable: [
        { text: "Name", value: "fullName", width: "160px" },
        { text: "Stellenprozent", value: "percentage" },
        { text: "Verträge", value: "contracts" },
        { text: "Pakete", value: "packageCount" },
        { text: "Anamnesebögen", value: "medicalHistory" },
        { text: "Trainingspläne", value: "workouts" },
        { text: "Versch. Kunden TP", value: "workoutsPerCustomer" },
        { text: "Kundeninteraktionen", value: "interactions.totalInteractions" },
        { text: "Termine", value: "appointments" },
        { text: "Upgrades", value: "upgrades" },
      ],
      overtimeVacationHeaders: [
        { text: "Trainer", value: "fullName", width: "150px" },
        { text: "Stellenprozent", value: "percentage" },
        { text: "Überstunden in (h)", value: "overTime" },
        { text: "Ferienstunden", value: "vacationHours" },
        { text: "Urlaubstage im " + this.$moment(this.datePickerRange[0]).format("MMM YY"), value: "vacationDaysCount" },
      ],
    }
  },
  created() {
    this.fetchData()
    this.fetchTerminations()
    this.fetchElevenDaysData()
    this.fetchWorkCalendarCenterUtilization()
  },
  methods: {
    comparisonRange() {
      if (!Array.isArray(this.datePickerRange) || !this.datePickerRange[0] || !this.datePickerRange[1]) return null
      const start = this.$moment(this.datePickerRange[0])
      const end = this.$moment(this.datePickerRange[1])
      if (!start.isValid() || !end.isValid()) return null
      return [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")]
    },
    async fetchWorkCalendarCenterUtilization() {
      const range = this.comparisonRange()
      if (!range) return
      const requestId = ++this.comparisonRequestSerial
      this.taskBreakdownOpen = false
      this.activeBreakdownMetricKey = null
      this.comparisonLoading = true
      this.comparisonError = ""
      try {
        const result = await api.shiftManagement.fetchWorkCalendarCenterUtilization({ range })
        if (requestId !== this.comparisonRequestSerial) return
        const employees = Array.isArray(result?.employees) ? result.employees : []
        this.workCalendarComparison = {
          meta: result?.meta || {},
          centers: Array.isArray(result?.centers) ? result.centers : [],
          employees,
        }
        this.selectPreferredComparisonProfiles(true)
      } catch (error) {
        if (requestId !== this.comparisonRequestSerial) return
        this.comparisonError = error?.response?.data?.message || "Der Center-Auslastungsvergleich konnte nicht geladen werden."
      } finally {
        if (requestId === this.comparisonRequestSerial) this.comparisonLoading = false
      }
    },
    selectPreferredComparisonProfiles(preferSelectedStudio = false) {
      const centers = Array.isArray(this.workCalendarComparison?.centers) ? this.workCalendarComparison.centers : []
      const employees = Array.isArray(this.workCalendarComparison?.employees) ? this.workCalendarComparison.employees : []
      const selectedCenter = centers.find((center) => String(center.id || "") === String(this.selectedStudioSingle || ""))
      const selectedCenterKey = selectedCenter?.key || null
      if (preferSelectedStudio || this.centerProfileKey !== selectedCenterKey) {
        this.centerProfileKey = selectedCenterKey
      }

      const selectedCenterEmployees = selectedCenterKey
        ? employees.filter((employee) => employee.centerKey === selectedCenterKey)
        : []
      const currentEmployeeAvailable = selectedCenterEmployees.some((employee) => employee.key === this.employeeProfileKey)
      if (preferSelectedStudio || !currentEmployeeAvailable) {
        this.employeeProfileKey = selectedCenterEmployees[0]?.key || null
      }
    },
    handleTaskBreakdownDataPointSelection(event, _chartContext, selection) {
      this.openTaskBreakdownFromRadar(selection, event)
    },
    resetTaskBreakdownForContext() {
      this.taskBreakdownOpen = false
      this.activeBreakdownMetricKey = null
    },
    toggleTaskBreakdown(metricKey) {
      const breakdown = this.availableRadarBreakdowns.find((entry) => entry.metricKey === metricKey)
      if (!breakdown) return
      const nextState = !(this.taskBreakdownOpen && this.activeBreakdownMetricKey === metricKey)
      this.activeBreakdownMetricKey = nextState ? metricKey : null
      this.taskBreakdownOpen = nextState
    },
    openTaskBreakdownFromRadar(selection, event = null) {
      const index = Number(selection?.dataPointIndex)
      const axisKey = Number.isInteger(index) ? this.comparisonRadarAxes[index]?.key || null : null
      const targetText = String(event?.target?.textContent || "").trim()
      const requestedBreakdown = this.radarBreakdownDefinitions.find(
        (breakdown) => breakdown.metricKey === axisKey || breakdown.label === targetText,
      )
      if (!requestedBreakdown) return
      const availableBreakdown = this.availableRadarBreakdowns.find(
        (breakdown) => breakdown.metricKey === requestedBreakdown.metricKey,
      )
      if (!availableBreakdown) return
      this.activeBreakdownMetricKey = availableBreakdown.metricKey
      this.taskBreakdownOpen = true
    },
    normalizeComparisonProfile(profile) {
      return {
        ...profile,
        metrics: profile?.metrics || {},
        appointmentSummary: profile?.appointmentSummary || { count: 0, minutes: 0, hours: 0 },
        appointmentBreakdown: Array.isArray(profile?.appointmentBreakdown) ? profile.appointmentBreakdown : [],
        amtliSummary: profile?.amtliSummary || { count: 0, minutes: 0, hours: 0 },
        amtliBreakdown: Array.isArray(profile?.amtliBreakdown) ? profile.amtliBreakdown : [],
        taskSummary: profile?.taskSummary || { count: 0, minutes: 0, hours: 0 },
        taskBreakdown: Array.isArray(profile?.taskBreakdown) ? profile.taskBreakdown : [],
      }
    },
    comparisonCenterDisplayLabel(center) {
      return center?.presentationOwnCenter ? `★ DEIN CENTER · ${center.label}` : center?.label || "Center"
    },
    normalizeRadarBreakdownItems(items) {
      return (Array.isArray(items) ? items : [])
        .map((item, index) => ({
          key: String(item?.key || `entry-${index}`),
          label: String(item?.label || "Eintrag"),
          count: Math.max(0, Number(item?.count || 0)),
          minutes: Math.max(0, Number(item?.minutes || 0)),
          hours: Math.max(0, Number(item?.hours || 0)),
          shareRate: Math.max(0, Number(item?.shareRate !== undefined ? item.shareRate : item?.taskShareRate || 0)),
          shiftRate: Math.max(0, Number(item?.shiftRate || 0)),
        }))
        .filter((item) => item.minutes > 0)
        .sort((a, b) => b.minutes - a.minutes || a.label.localeCompare(b.label, "de"))
    },
    formatTaskDuration(value) {
      const totalMinutes = Math.max(0, Math.round(Number(value || 0)))
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      if (!hours) return `${minutes} Min.`
      return minutes ? `${hours} h ${minutes} Min.` : `${hours} h`
    },
    formatTaskCount(value) {
      const count = Math.max(0, Math.round(Number(value || 0)))
      return `${count} ${count === 1 ? "Eintrag" : "Einträge"}`
    },
    toggleMobileTableMode() {
      this.mobileTableMode = this.mobileTableMode === "0" ? "xs" : "0"
    },
    async fetchData() {
      this.finishedLoading = false
      this.dashboardLoadingError = ""
      try {
        const [data, trainerVacations, dashboardTrainerInfos] = await Promise.all([
          api.fetchMontlyStatistcsForStudioRange(this.selectedStudioSingle, this.datePickerRange),
          api.fetchHolidayEntryEmployeeStudioManagement(this.selectedStudioSingle, "accepted"),
          api.fetchTrainerDashboardInfos({
            studios: [this.selectedStudioSingle],
            range: this.datePickerRange,
            includeAppointmentFacts: true,
            includeAppointmentStudioBreakdown: true,
            compactTrainerStatistics: true,
          }),
        ])

        this.startOfMonth = Array.isArray(data?.startOfMonthStat) ? data.startOfMonthStat[0] || null : null
        this.growthForMonth = data?.growthForMonth ?? null
        this.newContracts = data?.contracts?.[0] || { totalContracts: 0, publicContracts: 0 }
        this.trainerVacations = trainerVacations
        this.dashboardTrainerInfos = dashboardTrainerInfos
        this.notShownTrainers = this.dashboardTrainerInfos.filter((t) => !t.contracts && !t.workouts && !t.medicalHistory)
      } catch (error) {
        this.dashboardLoadingError = error?.response?.data?.message || "Die Monatsdaten konnten nicht vollständig geladen werden."
      } finally {
        this.finishedLoading = true
      }
    },
    async fetchElevenDaysData() {
      this.finishedLoadingElevenDaysData = false
      try {
        let data = await api.fetchMontlyStatistcsElevenDaysForStudioRange(this.selectedStudioSingle, this.datePickerRange)
        // NaN-Werte direkt bei der API-Antwort abfangen
        this.elevenDaysData = data.map((item) => ({
          ...item,
          quote: isNaN(parseFloat(item.quote)) ? 0 : parseFloat(item.quote),
          studioQuote: isNaN(parseFloat(item.studioQuote)) ? 0 : parseFloat(item.studioQuote),
        }))
      } catch (error) {
        this.$toast.error("Fehler beim Laden der 11 Tage Daten")
      }
      this.finishedLoadingElevenDaysData = true
    },
    async fetchTerminations() {
      this.finishedLoadingTerminations = false
      let data = await api.fetchMonthlyStatistcsForStudioTerminationsRange(this.selectedStudioSingle, this.datePickerRange)
      this.terminations = data.terminations
      this.finishedLoadingTerminations = true
    },

    // --- Methoden für Trainer Ziele ---
    openTrainerSelectDialog() {
      this.goalMonth = this.$moment(this.datePickerRange[0]).format("YYYY-MM")
      this.dialogTrainers = JSON.parse(JSON.stringify(this.filteredDashboardTrainerInfos))
      this.showTrainerSelectDialog = true
    },
    async fetchDialogTrainers() {
      this.isFetchingDialogTrainers = true
      let range = [this.$moment(this.goalMonth).startOf("month").toDate(), this.$moment(this.goalMonth).endOf("month").toDate()]
      let data = await api.fetchTrainerDashboardInfos({
        studios: [this.selectedStudioSingle],
        range: range,
        includeAppointmentFacts: true,
        compactTrainerStatistics: true,
      })
      this.dialogTrainers = data.filter((u) => !u.fullName.toLowerCase().includes("roodytrainer"))
      this.isFetchingDialogTrainers = false
    },
    getVacationDaysForTrainer(trainer, monthStr) {
      // Sicherstellen, dass .dates ein Array ist, um Fehler bei .reduce() zu vermeiden
      const entries = (this.trainerVacations || []).filter((v) => v.employee?.fullName === trainer.fullName && Array.isArray(v.dates))
      return entries.reduce((total, entry) => {
        return (
          total +
          entry.dates.reduce((sum, d) => {
            return this.$moment(d.date).format("YYYY-MM") === monthStr && d.duration > 0 ? sum + d.duration : sum
          }, 0)
        )
      }, 0)
    },
    calcBusinessDaysForMonth(monthStr) {
      let day = this.$moment(monthStr).startOf("month")
      let end = this.$moment(monthStr).endOf("month")
      let businessDays = 0
      while (day.isSameOrBefore(end, "day")) {
        if (day.day() !== 0 && day.day() !== 6) businessDays++
        day.add(1, "d")
      }
      return businessDays
    },
    openTargetDialog(trainer) {
      if (userService.hasRole("616616c4c659c875cad63525") || userService.hasRole("61661791c659c875cad63527")) return

      this.selectedTrainerForGoal = trainer
      this.vacationDays = this.getVacationDaysForTrainer(trainer, this.goalMonth)

      let studio = this.storeStudios.find((s) => s._id === (trainer.mainStudio?._id || trainer.mainStudio || trainer.studio[0]))
      let studioHolidays = 0
      if (studio && studio.vacationDays) {
        studioHolidays = studio.vacationDays.filter(
          (day) => this.$moment(this.goalMonth).format("YYYY-MM") === this.$moment(day.date).format("YYYY-MM"),
        ).length
      }

      let bDays = this.calcBusinessDaysForMonth(this.goalMonth)
      this.workHours = Math.round((bDays - studioHolidays) * ((8.4 * trainer.percentage) / 100) * 1000) / 1000 + " Std. (" + trainer.percentage + "%)"

      if (trainer.trainerGoal && trainer.trainerGoal.goals) {
        this.targets = { ...trainer.trainerGoal.goals }
      } else {
        this.targets = {
          contracts: null,
          anamneses: null,
          packages: null,
          workouts: null,
          workoutsPerCustomer: null,
          interactions: null,
          upgrades: null,
          appointmentsTarget: null,
          appointmentsActual: null,
        }
      }
      this.showTargetDialog = true
    },
    async saveTargets() {
      await api.saveTrainerGoalsForMonth({
        goals: this.targets,
        trainer: this.selectedTrainerForGoal._id,
        month: this.goalMonth,
      })

      let tDialog = this.dialogTrainers.find((t) => t._id === this.selectedTrainerForGoal._id)
      if (tDialog) {
        this.$set(tDialog, "trainerGoal", { month: this.goalMonth, goals: { ...this.targets } })
      }

      if (this.goalMonth === this.$moment(this.datePickerRange[0]).format("YYYY-MM")) {
        let tMain = this.dashboardTrainerInfos.find((t) => t._id === this.selectedTrainerForGoal._id)
        if (tMain) {
          this.$set(tMain, "trainerGoal", { month: this.goalMonth, goals: { ...this.targets } })
        }
      }

      this.$toast.success("Ziele gespeichert")
      this.showTargetDialog = false
    },

    appointmentActual(trainer) {
      if (trainer && trainer.appointmentsActual !== null && trainer.appointmentsActual !== undefined && trainer.appointmentsActual !== "") {
        return Number(trainer.appointmentsActual) || 0
      }
      const legacyValue = trainer?.trainerGoal?.goals?.appointmentsActual
      return legacyValue !== null && legacyValue !== undefined && legacyValue !== "" ? Number(legacyValue) || 0 : 0
    },
    appointmentSource(trainer) {
      if (["manual", "manual_missing", "workcalendar", "mixed", "mixed_missing"].includes(trainer?.appointmentsActualSource)) {
        return trainer.appointmentsActualSource
      }
      const legacyValue = trainer?.trainerGoal?.goals?.appointmentsActual
      return legacyValue !== null && legacyValue !== undefined && legacyValue !== "" ? "manual" : "workcalendar"
    },
    appointmentSourceLabel(trainer) {
      return {
        manual: "Bis 31.07.2026: historisch fest hinterlegter Ist-Wert",
        manual_missing: "Vor August 2026: historischer Ist-Wert fehlt",
        workcalendar: "Ab 01.08.2026: automatisch aus dem WorkCalendar",
        mixed: "Historische Ist-Werte + WorkCalendar ab 01.08.2026",
        mixed_missing: "Gemischt: mindestens ein historischer Ist-Wert fehlt",
      }[this.appointmentSource(trainer)]
    },
    appointmentSourceIcon(trainer) {
      const source = this.appointmentSource(trainer)
      if (["manual_missing", "mixed_missing"].includes(source)) return "mdi-alert-circle-outline"
      if (source === "manual") return "mdi-history"
      if (source === "mixed") return "mdi-source-merge"
      return "mdi-calendar-check-outline"
    },

    getTargetColor(actual, target) {
      if (target == null || target === 0) return "grey"
      const ratio = actual / target
      if (ratio <= 0.5) return "red"
      if (ratio < 1) return "orange"
      return "green"
    },
    getPackageColor(val) {
      if (val > 1.5) return "green"
      if (val < 0.8) return "red"
      return "orange"
    },
    getDeviationColor(value, avg) {
      return ""
      return "grey"
      if (value >= avg * 1.2) return "green"
      if (value <= avg * 0.8) return "red"
      return "orange"
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    comparisonMeta() {
      return this.workCalendarComparison?.meta || {}
    },
    comparisonCenters() {
      return (this.workCalendarComparison?.centers || []).map((center) => this.normalizeComparisonProfile(center))
    },
    comparisonEmployees() {
      return (this.workCalendarComparison?.employees || []).map((employee) => this.normalizeComparisonProfile(employee))
    },
    selectedComparisonCenter() {
      return (
        this.comparisonCenters.find(
          (center) => center.identifiable && String(center.id || "") === String(this.selectedStudioSingle || ""),
        ) || null
      )
    },
    canAnonymizeOtherCenters() {
      return Boolean(this.selectedComparisonCenter && this.comparisonCenters.filter((center) => center.identifiable).length > 1)
    },
    comparisonPresentationModeActive() {
      return this.canAnonymizeOtherCenters && this.anonymizeOtherCenters
    },
    comparisonPresentationDescription() {
      const centerName = this.selectedComparisonCenter?.label || "das ausgewählte Center"
      if (this.comparisonPresentationModeActive) {
        return `Nur ${centerName} bleibt benannt; alle anderen Center und Mitarbeitenden werden neutral angezeigt.`
      }
      return `Beim Aktivieren bleibt nur ${centerName} namentlich sichtbar.`
    },
    comparisonPresentationCenterLabels() {
      if (!this.comparisonPresentationModeActive) return {}
      const selectedKey = this.selectedComparisonCenter?.key
      const labels = {}
      this.comparisonCenters
        .filter((center) => center.key !== selectedKey)
        .sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), "de"))
        .forEach((center, index) => {
          labels[center.key] = `Vergleichscenter ${index + 1}`
        })
      return labels
    },
    displayedComparisonCenters() {
      const presentationLabels = this.comparisonPresentationCenterLabels
      const selectedKey = this.selectedComparisonCenter?.key
      return this.comparisonCenters.map((center) => {
        if (this.comparisonPresentationModeActive && center.key === selectedKey) {
          return { ...center, presentationOwnCenter: true }
        }
        const presentationLabel = presentationLabels[center.key]
        if (!presentationLabel) return center
        return { ...center, label: presentationLabel, identifiable: false, presentationAnonymized: true }
      })
    },
    displayedComparisonEmployees() {
      if (!this.comparisonPresentationModeActive) return this.comparisonEmployees
      const selectedKey = this.selectedComparisonCenter?.key
      const countByCenter = {}
      const employeeNumberByKey = {}
      this.comparisonEmployees
        .filter((employee) => employee.centerKey !== selectedKey)
        .sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), "de"))
        .forEach((employee) => {
          const employeeNumber = (countByCenter[employee.centerKey] || 0) + 1
          countByCenter[employee.centerKey] = employeeNumber
          employeeNumberByKey[employee.key] = `Mitarbeiter ${employeeNumber}`
        })

      return this.comparisonEmployees.map((employee) => {
        if (employee.centerKey === selectedKey) return { ...employee, presentationOwnCenter: true }
        const centerLabel = this.comparisonPresentationCenterLabels[employee.centerKey]
        if (!centerLabel) return employee
        return {
          ...employee,
          label: employeeNumberByKey[employee.key] || "Mitarbeiter",
          centerLabel,
          presentationAnonymized: true,
        }
      })
    },
    comparisonAnonymousCenterCount() {
      return this.displayedComparisonCenters.filter((center) => !center.identifiable).length
    },
    activeComparisonMetric() {
      return this.comparisonMetricOptions.find((metric) => metric.value === this.comparisonMetric) || this.comparisonMetricOptions[0]
    },
    activeComparisonDefinition() {
      if (this.comparisonView === "bars") return this.activeComparisonMetric?.definition || ""
      if (this.radarGroup === "shifts") {
        return "Schichtprofil in Prozent der Schichtanzahl. Wochenende ist eine zusätzliche Zeitlage und kann sich mit Früh, Spät oder Split überschneiden."
      }
      return "Zeitprofil in Prozent der Netto-Schichtzeit. Die weisse Fläche zeigt Zeit ohne gebuchten Kalendereintrag; Überschneidungen werden dort nur einmal gerechnet."
    },
    sortedComparisonCenters() {
      const rows = [...this.displayedComparisonCenters]
      const metric = this.comparisonMetric
      if (this.comparisonSort === "name") return rows.sort((a, b) => a.label.localeCompare(b.label, "de"))
      const direction = this.comparisonSort === "asc" ? 1 : -1
      return rows.sort((a, b) => {
        const difference = (Number(a.metrics?.[metric]) || 0) - (Number(b.metrics?.[metric]) || 0)
        return difference ? difference * direction : a.label.localeCompare(b.label, "de")
      })
    },
    comparisonBarHeight() {
      return Math.max(360, this.sortedComparisonCenters.length * 44 + 90)
    },
    comparisonBarSeries() {
      const metric = this.activeComparisonMetric
      return [
        {
          name: metric?.text || "Auslastung",
          data: this.sortedComparisonCenters.map((center) => ({
            x: this.comparisonCenterDisplayLabel(center),
            y: Number(center.metrics?.[this.comparisonMetric] || 0),
            fillColor: center.presentationOwnCenter ? "#22d3ee" : center.identifiable ? metric?.color || "#4f9cf9" : "#94a3b8",
          })),
        },
      ]
    },
    comparisonBarOptions() {
      return {
        chart: {
          type: "bar",
          background: "transparent",
          foreColor: "#cbd5e1",
          toolbar: { show: false },
          animations: { enabled: true, speed: 320 },
        },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 5, barHeight: "64%" },
        },
        dataLabels: {
          enabled: true,
          formatter: (value) => `${Number(value || 0).toFixed(1)}%`,
          style: { fontSize: "11px", fontWeight: 800, colors: ["#f8fafc"] },
          background: { enabled: true, foreColor: "#0f172a", borderRadius: 4, opacity: 1, padding: 4, borderWidth: 0 },
        },
        xaxis: {
          min: 0,
          max: 100,
          tickAmount: 5,
          labels: { formatter: (value) => `${Number(value || 0).toFixed(0)}%` },
        },
        yaxis: {
          labels: {
            maxWidth: this.$vuetify.breakpoint.xsOnly ? 150 : 280,
            style: {
              colors: this.sortedComparisonCenters.map((center) => (center.presentationOwnCenter ? "#67e8f9" : "#cbd5e1")),
              fontSize: "12px",
              fontWeight: 700,
            },
          },
        },
        grid: { borderColor: "rgba(255,255,255,0.08)", strokeDashArray: 4, padding: { left: 8, right: 24 } },
        legend: { show: false },
        tooltip: {
          theme: "dark",
          y: { formatter: (value) => `${Number(value || 0).toFixed(1)}%` },
        },
      }
    },
    comparisonBarChartKey() {
      return `${this.comparisonMetric}:${this.comparisonSort}:${this.comparisonPresentationModeActive}:${this.sortedComparisonCenters
        .map((center) => center.key)
        .join("|")}`
    },
    selectableCenterProfiles() {
      const selectedCenterKey = this.selectedComparisonCenter?.key
      if (!selectedCenterKey) return []
      return this.displayedComparisonCenters.filter((center) => center.key === selectedCenterKey)
    },
    selectableEmployeeProfiles() {
      const selectedCenterKey = this.selectedComparisonCenter?.key
      if (!selectedCenterKey) return []
      return this.displayedComparisonEmployees.filter((employee) => employee.centerKey === selectedCenterKey)
    },
    centerProfileOptions() {
      return this.selectableCenterProfiles.map((center) => ({ text: this.comparisonCenterDisplayLabel(center), value: center.key }))
    },
    employeeProfileOptions() {
      return this.selectableEmployeeProfiles.map((employee) => ({
        text: `${employee.label} · ${employee.centerLabel}${employee.presentationOwnCenter ? " · Dein Center" : ""}`,
        value: employee.key,
      }))
    },
    activeRadarItem() {
      if (this.comparisonView === "employeeRadar") {
        return this.selectableEmployeeProfiles.find((employee) => employee.key === this.employeeProfileKey) || null
      }
      return this.selectableCenterProfiles.find((center) => center.key === this.centerProfileKey) || null
    },
    activeRadarTitle() {
      return this.activeRadarItem?.label || ""
    },
    activeRadarSubtitle() {
      return this.comparisonView === "employeeRadar" ? this.activeRadarItem?.centerLabel || "" : ""
    },
    radarBreakdownDefinitions() {
      return [
        {
          metricKey: "appointmentRate",
          breakdownField: "appointmentBreakdown",
          summaryField: "appointmentSummary",
          label: "Termine",
          icon: "mdi-calendar-clock-outline",
          question: "Welche Termine hat",
          timeLabel: "Terminzeit",
          itemPlural: "Terminarten",
          color: "#4f9cf9",
          soft: "rgba(79, 156, 249, 0.16)",
          textColor: "#bfdbfe",
        },
        {
          metricKey: "amtliRate",
          breakdownField: "amtliBreakdown",
          summaryField: "amtliSummary",
          label: "Ämtli",
          icon: "mdi-clipboard-check-outline",
          question: "Welche Ämtli hat",
          timeLabel: "Ämtlizeit",
          itemPlural: "Ämtli",
          color: "#a78bfa",
          soft: "rgba(167, 139, 250, 0.16)",
          textColor: "#ddd6fe",
        },
        {
          metricKey: "taskRate",
          breakdownField: "taskBreakdown",
          summaryField: "taskSummary",
          label: "Aufgaben",
          icon: "mdi-format-list-checks",
          question: "Welche Aufgaben hat",
          timeLabel: "Aufgabenzeit",
          itemPlural: "Aufgaben",
          color: "#f472b6",
          soft: "rgba(244, 114, 182, 0.16)",
          textColor: "#fbcfe8",
        },
      ]
    },
    availableRadarBreakdowns() {
      const supportedView = this.comparisonView === "employeeRadar" || this.comparisonView === "centerRadar"
      const inaccessibleCenter = this.comparisonView === "centerRadar" && this.activeRadarItem?.identifiable !== true
      if (
        !supportedView ||
        this.radarGroup !== "time" ||
        !this.activeRadarItem ||
        this.activeRadarItem.presentationAnonymized ||
        inaccessibleCenter
      ) {
        return []
      }
      return this.radarBreakdownDefinitions
        .map((definition) => {
          const rawSummary = this.activeRadarItem?.[definition.summaryField] || {}
          return {
            ...definition,
            items: this.normalizeRadarBreakdownItems(this.activeRadarItem?.[definition.breakdownField]),
            summary: {
              count: Math.max(0, Number(rawSummary.count || 0)),
              minutes: Math.max(0, Number(rawSummary.minutes || 0)),
              hours: Math.max(0, Number(rawSummary.hours || 0)),
            },
          }
        })
        .filter((definition) => definition.items.length > 0)
    },
    activeRadarBreakdown() {
      return (
        this.availableRadarBreakdowns.find((breakdown) => breakdown.metricKey === this.activeBreakdownMetricKey) || null
      )
    },
    activeTaskBreakdown() {
      return this.activeRadarBreakdown?.items || []
    },
    canOpenTaskBreakdown() {
      return Boolean(this.activeRadarBreakdown && this.activeTaskBreakdown.length > 0)
    },
    taskBreakdownChartItems() {
      if (this.activeTaskBreakdown.length <= 6) return this.activeTaskBreakdown
      const primaryTasks = this.activeTaskBreakdown.slice(0, 5)
      const remainingTasks = this.activeTaskBreakdown.slice(5)
      const additionalTasks = remainingTasks.reduce(
        (summary, task) => ({
          ...summary,
          count: summary.count + task.count,
          minutes: summary.minutes + task.minutes,
          hours: summary.hours + task.hours,
          shareRate: summary.shareRate + task.shareRate,
          shiftRate: summary.shiftRate + task.shiftRate,
        }),
        {
          key: "__additional_entries__",
          label: `Weitere (${remainingTasks.length})`,
          count: 0,
          minutes: 0,
          hours: 0,
          shareRate: 0,
          shiftRate: 0,
        },
      )
      return [...primaryTasks, additionalTasks]
    },
    taskBreakdownUsesRadar() {
      return this.taskBreakdownChartItems.length >= 3
    },
    activeRadarPalette() {
      if (this.radarGroup === "shifts") {
        return { line: "#a78bfa", soft: "rgba(167, 139, 250, 0.16)", text: "#ddd6fe" }
      }
      return { line: "#4f9cf9", soft: "rgba(79, 156, 249, 0.16)", text: "#bfdbfe" }
    },
    comparisonRadarAxes() {
      if (this.radarGroup === "shifts") {
        return [
          { label: "Früh", key: "morningRate" },
          { label: "Spät", key: "lateRate" },
          { label: "Split", key: "splitRate" },
          { label: "Wochenende", key: "weekendRate" },
        ]
      }
      return [
        { label: "Termine", key: "appointmentRate" },
        { label: "Trainerpoint", key: "trainerpointRate" },
        { label: "Theke", key: "thekeRate" },
        { label: "Ämtli", key: "amtliRate" },
        { label: "Aufgaben", key: "taskRate" },
        { label: "Weisse Fläche", key: "whiteSpaceRate" },
      ]
    },
    comparisonRadarSeries() {
      return [
        {
          name: this.radarGroup === "shifts" ? "Schichtanteil" : "Zeitanteil",
          data: this.comparisonRadarAxes.map((axis) => Number(this.activeRadarItem?.metrics?.[axis.key] || 0)),
        },
      ]
    },
    comparisonRadarMax() {
      const values = this.comparisonRadarAxes.map((axis) => Number(this.activeRadarItem?.metrics?.[axis.key] || 0))
      const peak = Math.max(0, ...values.filter((value) => Number.isFinite(value)))
      if (!peak) return 10
      return Math.min(100, Math.max(10, Math.ceil((peak * 1.05) / 10) * 10))
    },
    comparisonRadarHeight() {
      return this.$vuetify.breakpoint.xsOnly ? 390 : 500
    },
    comparisonRadarOptions() {
      const palette = this.activeRadarPalette
      const forwardRadarBreakdownEvent = (event, _chartContext, selection) => this.openTaskBreakdownFromRadar(selection, event)
      const breakdownMarkers = this.availableRadarBreakdowns
        .map((breakdown) => {
          const dataPointIndex = this.comparisonRadarAxes.findIndex((axis) => axis.key === breakdown.metricKey)
          if (dataPointIndex < 0) return null
          return {
            seriesIndex: 0,
            dataPointIndex,
            fillColor: breakdown.color,
            strokeColor: "#f8fafc",
            size: this.activeBreakdownMetricKey === breakdown.metricKey ? 8 : 7,
          }
        })
        .filter(Boolean)
      return {
        chart: {
          type: "radar",
          background: "transparent",
          foreColor: "#dbe7f5",
          toolbar: { show: false },
          animations: { enabled: true, speed: 320 },
          events: {
            click: forwardRadarBreakdownEvent,
            markerClick: forwardRadarBreakdownEvent,
          },
        },
        xaxis: {
          categories: this.comparisonRadarAxes.map((axis) => axis.label),
          labels: { style: { colors: this.comparisonRadarAxes.map(() => "#dbe7f5"), fontSize: "12px", fontWeight: 700 } },
        },
        yaxis: { min: 0, max: this.comparisonRadarMax, tickAmount: 5, show: false },
        plotOptions: {
          radar: {
            size: this.$vuetify.breakpoint.xsOnly ? 105 : this.$vuetify.breakpoint.smAndDown ? 145 : 190,
            polygons: {
              strokeColors: "rgba(148, 163, 184, 0.24)",
              connectorColors: "rgba(148, 163, 184, 0.2)",
              fill: { colors: ["rgba(79, 156, 249, 0.025)", "rgba(148, 163, 184, 0.045)"] },
            },
          },
        },
        colors: [palette.line],
        stroke: { width: 3 },
        fill: { opacity: 0.24 },
        markers: {
          size: 5,
          strokeColors: "#111827",
          strokeWidth: 2,
          hover: { size: 7 },
          discrete: breakdownMarkers,
        },
        dataLabels: {
          enabled: true,
          formatter: (value) => (Number(value || 0) > 0 ? `${Number(value).toFixed(0)}%` : ""),
          style: { fontSize: "11px", fontWeight: 800, colors: ["#f8fafc"] },
          background: { enabled: false },
          dropShadow: { enabled: true, top: 1, left: 1, blur: 2, color: "#020617", opacity: 0.9 },
        },
        legend: { show: false },
        tooltip: { theme: "dark", y: { formatter: (value) => `${Number(value || 0).toFixed(1)}%` } },
      }
    },
    comparisonRadarChartKey() {
      return `${this.comparisonView}:${this.radarGroup}:${this.activeRadarItem?.key || "none"}:${this.comparisonRadarMax}:${
        this.comparisonPresentationModeActive
      }`
    },
    taskBreakdownRadarSeries() {
      return [
        {
          name: `Anteil der ${this.activeRadarBreakdown?.timeLabel || "Detailzeit"}`,
          data: this.taskBreakdownChartItems.map((task) => Number(task.shareRate || 0)),
        },
      ]
    },
    taskBreakdownRadarMax() {
      const peak = Math.max(0, ...this.taskBreakdownChartItems.map((task) => Number(task.shareRate || 0)))
      if (!peak) return 10
      return Math.min(100, Math.max(10, Math.ceil((peak * 1.05) / 10) * 10))
    },
    taskBreakdownRadarHeight() {
      return this.$vuetify.breakpoint.xsOnly ? 360 : 430
    },
    taskBreakdownRadarOptions() {
      const color = this.activeRadarBreakdown?.color || "#f472b6"
      const soft = this.activeRadarBreakdown?.soft || "rgba(244, 114, 182, 0.16)"
      const textColor = this.activeRadarBreakdown?.textColor || "#fbcfe8"
      const timeLabel = this.activeRadarBreakdown?.timeLabel || "Detailzeit"
      return {
        chart: {
          type: "radar",
          background: "transparent",
          foreColor: "#f8fafc",
          toolbar: { show: false },
          animations: { enabled: true, speed: 320 },
        },
        xaxis: {
          categories: this.taskBreakdownChartItems.map((task) => task.label),
          labels: {
            formatter: (value) => {
              const label = String(value || "")
              const maxLength = this.$vuetify.breakpoint.xsOnly ? 14 : 22
              return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label
            },
            style: {
              colors: this.taskBreakdownChartItems.map(() => "#f8fafc"),
              fontSize: this.$vuetify.breakpoint.xsOnly ? "10px" : "12px",
              fontWeight: 700,
            },
          },
        },
        yaxis: { min: 0, max: this.taskBreakdownRadarMax, tickAmount: 5, show: false },
        plotOptions: {
          radar: {
            size: this.$vuetify.breakpoint.xsOnly ? 96 : this.$vuetify.breakpoint.smAndDown ? 135 : 165,
            polygons: {
              strokeColors: soft,
              connectorColors: soft,
              fill: { colors: [soft, "rgba(148, 163, 184, 0.04)"] },
            },
          },
        },
        colors: [color],
        stroke: { width: 3 },
        fill: { opacity: 0.24 },
        markers: { size: 5, strokeColors: "#111827", strokeWidth: 2, hover: { size: 7 } },
        dataLabels: {
          enabled: true,
          formatter: (value) => (Number(value || 0) > 0 ? `${Number(value).toFixed(0)}%` : ""),
          style: { fontSize: "11px", fontWeight: 800, colors: [textColor] },
          background: { enabled: false },
          dropShadow: { enabled: true, top: 1, left: 1, blur: 2, color: "#020617", opacity: 0.9 },
        },
        legend: { show: false },
        tooltip: {
          theme: "dark",
          y: {
            formatter: (value, context) => {
              const task = this.taskBreakdownChartItems[Number(context?.dataPointIndex)]
              const details = task ? ` · ${this.formatTaskDuration(task.minutes)} · ${this.formatTaskCount(task.count)}` : ""
              return `${Number(value || 0).toFixed(1)} % der ${timeLabel}${details}`
            },
          },
        },
      }
    },
    taskBreakdownRadarChartKey() {
      return `${this.activeRadarItem?.key || "none"}:${this.activeBreakdownMetricKey || "none"}:${this.taskBreakdownRadarMax}:${this.taskBreakdownChartItems
        .map((task) => `${task.key}:${task.minutes}`)
        .join("|")}`
    },
    overtimeVacationItems() {
      const start = this.$moment(this.datePickerRange[0]).startOf("month")
      const end = this.$moment(this.datePickerRange[0]).endOf("month")
      return this.dashboardTrainerInfos.map((trainer) => {
        const vacEntries = (this.trainerVacations || []).filter((v) => v.employee.fullName === trainer.fullName && v.dates?.length > 0)
        let vacationDatesList = []
        vacEntries.forEach((entry) => {
          entry.dates.forEach((d) => {
            if (this.$moment(d.date).isBetween(start, end, "day", "[]")) {
              if (d.duration > 0) vacationDatesList.push({ date: d.date, reason: entry.reason || "kein Grund angegeben" })
            }
          })
        })
        return {
          fullName: trainer.fullName,
          percentage: trainer.percentage,
          timeTrackerPercentage: trainer.timeTrackerPercentage || trainer.percentage || 100, // Fallback gegen Division durch Null
          overTime: typeof trainer.overTime === "number" ? trainer.overTime : "keine hinterlegt",
          vacationHours: typeof trainer.mainStudio?.vacationHours === "number" ? trainer.mainStudio.vacationHours : "keine hinterlegt",
          vacationDaysCount: vacationDatesList.length,
          vacationDatesList,
        }
      })
    },
    averages() {
      if (!this.elevenDaysData || !this.elevenDaysData.length)
        return { activated: 0, accepted: 0, declined: 0, anamnese: 0, anamneseEleven: 0, quote: 0, studioQuote: 0 }
      const sums = this.elevenDaysData.reduce(
        (acc, cur) => {
          acc.activated += cur.activated || 0
          acc.accepted += cur.accepted || 0
          acc.declined += cur.declined || 0
          acc.anamnese += cur.anamnese || 0
          acc.anamneseEleven += cur.anamneseEleven || 0
          acc.quote += parseFloat(cur.quote) || 0
          acc.studioQuote += cur.studioQuote || 0
          return acc
        },
        { activated: 0, accepted: 0, declined: 0, anamnese: 0, anamneseEleven: 0, quote: 0, studioQuote: 0 },
      )
      const len = this.elevenDaysData.length || 1
      return {
        activated: sums.activated / len,
        accepted: sums.accepted / len,
        declined: sums.declined / len,
        anamnese: sums.anamnese / len,
        anamneseEleven: sums.anamneseEleven / len,
        quote: sums.quote / len,
        studioQuote: sums.studioQuote / len,
      }
    },
    filteredDashboardTrainerInfos() {
      let arr = JSON.parse(JSON.stringify(this.dashboardTrainerInfos))
      let totalPercentages = 0
      arr = arr.map((u) => {
        u.studio = u.studio.map((id) => this.storeStudios.find((s) => s._id === id)?.name || "zus. Team").join(", ")
        totalPercentages += u.allPercentages.find((p) => p._id === this.selectedStudioSingle)?.percentage || 0
        if (this.percentagesTo100 && u.percentage < 100) {
          u.contracts = parseFloat(((u.contracts / u.percentage) * 100).toFixed(2))
          u.interactions.totalInteractions = parseFloat(((u.interactions.totalInteractions / u.percentage) * 100).toFixed(2))
          u.medicalHistory = parseFloat(((u.medicalHistory / u.percentage) * 100).toFixed(2))
          u.workouts = parseFloat(((u.workouts / u.percentage) * 100).toFixed(2))
          u.workoutsPerCustomer = parseFloat(((u.workoutsPerCustomer / u.percentage) * 100).toFixed(2))
          u.upgrades = parseFloat(((u.upgrades / u.percentage) * 100).toFixed(2))
          u.percentage = 100
        }
        return u
      })
      arr = arr.filter((u) => !u.fullName.toLowerCase().includes("roodytrainer"))
      if (this.activeSorting) {
        arr.sort((a, b) => b[this.activeSorting] - a[this.activeSorting])
      }
      this.totalPercentages = totalPercentages
      return arr
    },
  },
  watch: {
    comparisonView() {
      this.resetTaskBreakdownForContext()
    },
    radarGroup() {
      this.resetTaskBreakdownForContext()
    },
    centerProfileKey() {
      this.resetTaskBreakdownForContext()
    },
    employeeProfileKey() {
      this.resetTaskBreakdownForContext()
    },
    anonymizeOtherCenters() {
      this.resetTaskBreakdownForContext()
    },
    async selectedStudioSingle() {
      this.resetTaskBreakdownForContext()
      this.fetchData()
      this.fetchTerminations()
      this.fetchElevenDaysData()
      this.selectPreferredComparisonProfiles(true)
    },
    async datePickerRange() {
      if (this.datePickerRange[0] && this.datePickerRange[1]) {
        this.fetchData()
        this.fetchTerminations()
        this.fetchElevenDaysData()
        this.fetchWorkCalendarCenterUtilization()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .apexcharts-menu-open {
  background-color: #212121 !important;
  height: 80vh;
}

.monthly-statistics-loading {
  min-height: calc(100vh - 32px);
}

.monthly-statistics-loading__card {
  position: relative;
  width: 100%;
  max-width: 720px;
  overflow: hidden;
  border: 1px solid rgba(79, 156, 249, 0.2) !important;
  background-image: radial-gradient(circle at 50% 0%, rgba(79, 156, 249, 0.16), transparent 44%),
    linear-gradient(180deg, rgba(15, 23, 42, 0.12), transparent) !important;
}

.monthly-statistics-loading__indicator {
  position: relative;
  display: grid;
  width: 104px;
  height: 104px;
  place-items: center;

  &::before {
    position: absolute;
    border-radius: 50%;
    animation: monthly-statistics-loader-pulse 1.8s ease-in-out infinite;
    background: rgba(79, 156, 249, 0.14);
    content: "";
    inset: 10px;
  }
}

.monthly-statistics-loading__eyebrow {
  color: #7dd3fc;
  letter-spacing: 0.14em;
}

.monthly-statistics-loading__skeleton {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.monthly-statistics-loading__skeleton-card {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.32);
}

.monthly-statistics-loading__skeleton-icon,
.monthly-statistics-loading__skeleton-line {
  position: relative;
  display: block;
  overflow: hidden;
  background: rgba(148, 163, 184, 0.16);

  &::after {
    position: absolute;
    animation: monthly-statistics-loader-shimmer 1.45s ease-in-out infinite;
    background: linear-gradient(90deg, transparent, rgba(226, 232, 240, 0.2), transparent);
    content: "";
    inset: 0;
    transform: translateX(-100%);
  }
}

.monthly-statistics-loading__skeleton-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  border-radius: 10px;
}

.monthly-statistics-loading__skeleton-line {
  width: 100%;
  height: 8px;
  margin: 7px 0;
  border-radius: 999px;
}

.monthly-statistics-loading__skeleton-line--short {
  width: 58%;
}

@keyframes monthly-statistics-loader-pulse {
  0%,
  100% {
    opacity: 0.45;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@keyframes monthly-statistics-loader-shimmer {
  100% {
    transform: translateX(100%);
  }
}

.dashboard-card {
  position: relative;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.4) !important;
  }
}

.card-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #9e9e9e;
  font-weight: 700;
  line-height: 1.2;
}

.accent-line {
  position: absolute;
  bottom: 0;
  left: 20px;
  right: 20px;
  height: 3px;
  border-radius: 3px 3px 0 0;
  opacity: 0.9;
}

.modern-table {
  border: 1px solid rgba(255, 255, 255, 0.05) !important;

  ::v-deep .v-data-table__wrapper {
    overflow-x: auto !important;
  }

  ::v-deep th {
    text-transform: uppercase;
    font-size: 0.75rem !important;
    letter-spacing: 0.5px;
    color: #9e9e9e !important;
    white-space: nowrap !important;
  }

  ::v-deep td {
    white-space: nowrap !important;
    padding: 0 16px !important;
  }
}

.appointment-actual-readonly {
  display: flex;
  min-height: 64px;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  border: 1px solid rgba(33, 150, 243, 0.35);
  border-radius: 8px;
  background: rgba(33, 150, 243, 0.08);

  > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
  }

  small,
  span {
    color: #90a4ae;
    font-size: 0.68rem;
    line-height: 1.25;
  }

  strong {
    margin: 1px 0;
    color: white;
    font-size: 1.15rem;
  }
}

.utilization-panel {
  border: 1px solid rgba(148, 163, 184, 0.14) !important;
  background-image: radial-gradient(circle at 92% 0%, rgba(79, 156, 249, 0.12), transparent 34%),
    linear-gradient(180deg, rgba(15, 23, 42, 0.1), transparent 46%) !important;
}

.utilization-panel__header {
  min-height: 84px;
}

.utilization-controls {
  gap: 4px 0;
}

.utilization-presentation-mode {
  min-height: 66px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.28);
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.utilization-presentation-mode--active {
  border-color: rgba(79, 156, 249, 0.45);
  background: rgba(79, 156, 249, 0.1);
}

.utilization-presentation-mode__copy {
  min-width: 220px;
}

.utilization-own-center-chip {
  border: 1px solid rgba(34, 211, 238, 0.35) !important;
  box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.07);
  letter-spacing: 0.025em;
}

.utilization-radar-profile--own {
  border: 1px solid rgba(34, 211, 238, 0.35) !important;
  box-shadow: 0 0 18px rgba(34, 211, 238, 0.12);
}

.utilization-presentation-mode__switch {
  flex: 0 0 auto;

  ::v-deep .v-label {
    color: #e2e8f0;
    font-weight: 600;
  }
}

.utilization-definition {
  min-height: 34px;
  max-width: 980px;
  line-height: 1.45;
}

.utilization-chart-shell,
.utilization-radar-shell {
  width: 100%;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.42);
}

.utilization-radar-shell {
  padding: 20px 12px 8px;
}

.utilization-task-trigger {
  min-height: 46px;
}

.utilization-task-trigger__hint {
  max-width: 520px;
}

.utilization-task-detail {
  border-top: 1px solid var(--breakdown-soft, rgba(244, 114, 182, 0.22));
  background: linear-gradient(180deg, var(--breakdown-soft, rgba(244, 114, 182, 0.07)), rgba(15, 23, 42, 0.08));
}

.utilization-task-detail__header {
  gap: 8px;
}

.utilization-task-detail__eyebrow {
  color: var(--breakdown-text, #f9a8d4);
  letter-spacing: 0.11em;
}

.utilization-task-list {
  display: grid;
  max-height: 320px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  overflow-y: auto;
}

.utilization-task-list__item {
  min-width: 0;
  border: 1px solid var(--breakdown-soft, rgba(244, 114, 182, 0.15));
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.34);

  .flex-grow-1 {
    min-width: 0;
  }
}

.utilization-task-list__dot {
  width: 9px;
  height: 9px;
  flex: 0 0 9px;
  border-radius: 50%;
  background: var(--breakdown-color, #f472b6);
  box-shadow: 0 0 0 4px var(--breakdown-soft, rgba(244, 114, 182, 0.12));
}

.utilization-task-list__rate {
  flex: 0 0 auto;
  color: var(--breakdown-text, #fbcfe8);
  font-size: 0.86rem;
}

.utilization-state {
  min-height: 210px;
}

.utilization-state--loading {
  border: 1px solid rgba(79, 156, 249, 0.13);
  border-radius: 14px;
  background: radial-gradient(circle at 50% 18%, rgba(79, 156, 249, 0.1), transparent 36%), rgba(15, 23, 42, 0.22);
}

.utilization-state__indicator {
  display: grid;
  width: 70px;
  height: 70px;
  place-items: center;
  border-radius: 50%;
  background: rgba(79, 156, 249, 0.08);
}

.utilization-state__progress {
  max-width: 360px;
}

.utilization-privacy {
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(71, 85, 105, 0.12);
  line-height: 1.45;
}

::v-deep .utilization-panel .apexcharts-canvas,
::v-deep .utilization-panel .apexcharts-svg {
  background: transparent !important;
}

::v-deep .utilization-panel .apexcharts-tooltip,
::v-deep .utilization-panel .apexcharts-xaxistooltip {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background: #1f2937 !important;
  color: white !important;
}

@media (max-width: 600px) {
  .monthly-statistics-loading {
    min-height: calc(100vh - 16px);
  }

  .monthly-statistics-loading__skeleton {
    grid-template-columns: minmax(0, 1fr);
  }

  .utilization-panel__header {
    align-items: flex-start !important;
  }

  .utilization-chart-shell {
    overflow-x: auto;
  }

  .utilization-radar-shell {
    padding-right: 0;
    padding-left: 0;
  }

  .utilization-task-trigger {
    padding-right: 16px !important;
    padding-left: 16px !important;
  }

  .utilization-task-trigger__hint {
    width: 100%;
    justify-content: center;
    text-align: center;
  }

  .utilization-task-detail__header {
    display: block !important;
  }

  .utilization-task-list {
    grid-template-columns: minmax(0, 1fr);
  }

  .utilization-presentation-mode__switch {
    width: 100%;
    margin-top: 12px !important;
  }

  ::v-deep .utilization-panel .apexcharts-yaxis-label,
  ::v-deep .utilization-panel .apexcharts-xaxis-label {
    font-size: 10px !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .monthly-statistics-loading__indicator::before,
  .monthly-statistics-loading__skeleton-icon::after,
  .monthly-statistics-loading__skeleton-line::after {
    animation: none;
  }
}
</style>
